import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, Stack, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import beepSound from '../../sounds/beep.wav'; // Ensure the path is correct

const Timer = () => {
  const [workMinutes, setWorkMinutes] = useState(0);
  const [workSeconds, setWorkSeconds] = useState(50);
  const [restMinutes, setRestMinutes] = useState(0);
  const [restSeconds, setRestSeconds] = useState(10);
  const [timeLeft, setTimeLeft] = useState(50);
  const [isRunning, setIsRunning] = useState(false);
  const [isWorkPhase, setIsWorkPhase] = useState(true);

  const beep = useRef(null);

  useEffect(() => {
    beep.current = new Audio(beepSound);
  }, []);

  useEffect(() => {
    let timer;
    if (isRunning) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 4 && prev > 0) {
            if (beep.current) beep.current.play();
          }
          if (prev === 1) {
            if (isWorkPhase) {
              setIsWorkPhase(false);
              return restMinutes * 60 + restSeconds;
            } else {
              setIsWorkPhase(true);
              return workMinutes * 60 + workSeconds;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRunning, isWorkPhase, workMinutes, workSeconds, restMinutes, restSeconds]);

  const handleStart = () => {
    setTimeLeft(isWorkPhase ? workMinutes * 60 + workSeconds : restMinutes * 60 + restSeconds);
    setIsRunning(true);
  };

  const handleStop = () => setIsRunning(false);

  const handleReset = () => {
    setIsRunning(false);
    setIsWorkPhase(true);
    setWorkMinutes(0);
    setWorkSeconds(0);
    setRestMinutes(0);
    setRestSeconds(0);
    setTimeLeft(0);
  };

  // Generate minute and second options
  const minuteOptions = Array.from({ length: 61 }, (_, i) => i);
  const secondOptions = Array.from({ length: 60 }, (_, i) => i);

  return (
    <Stack
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '20px',
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          p: '20px',
          borderRadius: '50%',
          width: { xs: '350px', sm: '500px', md: '600px', lg: '600px', xl: '600px' },
          height: { xs: '350px', sm: '500px', md: '600px', lg: '600px', xl: '600px' },
          color: '#fff',
          border: '3px solid black',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '0 auto',
        }}
      >
        <Typography
          variant="h4"
          fontWeight="700"
          sx={{ color: isWorkPhase ? '#ff6347' : '#1e90ff' }}
          fontFamily="GveretLevin"
        >
          {isWorkPhase ? 'זמן עבודה' : 'זמן מנוחה'}
        </Typography>
        <Box
          sx={{
            fontFamily: "'GveretLevin', monospace",
            fontSize: { xs: '40px', sm: '60px', md: '80px' },
            fontWeight: 'bold',
            color: '#000',
            m: '5px',
            background: '#fff',
            border: '3px solid black',
            borderRadius: '8px',
            padding: { xs: '5px 10px', sm: '10px 20px', md: '20px 40px' },
            marginTop: '20px',
          }}
        >
          {`${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(
            2,
            '0'
          )}`}
        </Box>

        <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: '10px' }}>
          <Button variant="contained" color="success" onClick={handleStart}>
            Start
          </Button>
          <Button variant="contained" color="error" onClick={handleStop}>
            Stop
          </Button>
          <Button variant="contained" color="warning" onClick={handleReset}>
            Reset
          </Button>
        </Stack>
      </Box>

      {/* Work and Rest Intervals with Scrollable Selects */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="center"
        alignItems="right"
        textAlign="center"
        spacing={2}
        sx={{ mt: '20px' }}
      >
        {/* Work Interval */}
        <Box>
          <Typography fontFamily="GveretLevin" fontWeight="700" sx={{ color: '#000' }}>
            אינטרבל עבודה
          </Typography>
          <FormControl
            variant="outlined"
            sx={{ width: '100px', m: '5px', background: '#fff', borderRadius: '8px' }}
          >
            <InputLabel id="work-minutes-label">דקות</InputLabel>
            <Select
              labelId="work-minutes-label"
              value={workMinutes}
              onChange={(e) => setWorkMinutes(e.target.value)}
              label="דקות"
            >
              {minuteOptions.map((min) => (
                <MenuItem key={min} value={min}>
                  {min}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            sx={{ width: '100px', m: '5px', background: '#fff', borderRadius: '8px' }}
          >
            <InputLabel id="work-seconds-label">שניות</InputLabel>
            <Select
              labelId="work-seconds-label"
              value={workSeconds}
              onChange={(e) => setWorkSeconds(e.target.value)}
              label="שניות"
            >
              {secondOptions.map((sec) => (
                <MenuItem key={sec} value={sec}>
                  {sec}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Rest Interval */}
        <Box direction="rtl" alignItems="right">
          <Typography fontFamily="GveretLevin" fontWeight="700" sx={{ color: '#000' }}>
            אינטרבל מנוחה
          </Typography>
          <FormControl
            variant="outlined"
            sx={{ width: '100px', m: '5px', background: '#fff', borderRadius: '8px' }}
          >
            <InputLabel id="rest-minutes-label">דקות</InputLabel>
            <Select
              labelId="rest-minutes-label"
              value={restMinutes}
              onChange={(e) => setRestMinutes(e.target.value)}
              label="דקות"
            >
              {minuteOptions.map((min) => (
                <MenuItem key={min} value={min}>
                  {min}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            sx={{ width: '100px', m: '5px', background: '#fff', borderRadius: '8px' }}
          >
            <InputLabel id="rest-seconds-label">שניות</InputLabel>
            <Select
              labelId="rest-seconds-label"
              value={restSeconds}
              onChange={(e) => setRestSeconds(e.target.value)}
              label="שניות"
            >
              {secondOptions.map((sec) => (
                <MenuItem key={sec} value={sec}>
                  {sec}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </Stack>
  );
};

export default Timer;
